"use client";
import { createLink } from "@gluestack-ui/core/link/creator";
import { Pressable, Text } from "react-native";

import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import { tva, withStyleContext } from "@gluestack-ui/utils/nativewind-utils";
import { cssInterop } from "nativewind";

import React from "react";
export const UILink = createLink({
  Root: withStyleContext(Pressable),
  Text: Text,
});

cssInterop(UILink, { className: "style" });
cssInterop(UILink.Text, { className: "style" });

const linkStyle = tva({
  base: "group/link web:outline-0 data-[disabled=true]:web:cursor-not-allowed data-[focus-visible=true]:web:ring-2 data-[focus-visible=true]:web:ring-indicator-primary data-[focus-visible=true]:web:outline-0 data-[disabled=true]:opacity-4 ",
});

const linkTextStyle = tva({
  base: "no-underline data-[hover=true]:underline font-normal font-body web:tracking-sm web:my-0 web:bg-transparent web:border-0 web:box-border web:display-inline web:list-none web:margin-0 web:padding-0 web:position-relative web:text-start web:whitespace-pre-wrap web:word-wrap-break-word",

  variants: {
    action: {
      muted:
        "text-typography-600 data-[hover=true]:text-typography-700 data-[active=true]:text-typography-800",
      primary:
        "text-typography-700 data-[hover=true]:text-typography-700 data-[active=true]:text-typography-800",
      info: "text-info-700 data-[hover=true]:text-info-600 data-[active=true]:text-info-700",
      brand:
        "text-brand-500 data-[hover=true]:text-brand-600 data-[active=true]:text-brand-700",
    },
    isTruncated: {
      true: "web:truncate",
    },
    bold: {
      true: "font-bold",
    },
    underline: {
      true: "underline",
    },
    strikeThrough: {
      true: "line-through",
    },
    size: {
      none: "",
      "2xs": "text-2xs",
      xs: "text-xs",
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
      "4xl": "text-4xl",
      "5xl": "text-5xl",
      "6xl": "text-6xl",
    },
    sub: {
      true: "text-xs",
    },
    italic: {
      true: "italic",
    },
    highlight: {
      true: "bg-yellow-500",
    },
  },
});

type ILinkProps = React.ComponentProps<typeof UILink> &
  VariantProps<typeof linkStyle> & { className?: string };

const Link = React.forwardRef<React.ComponentRef<typeof UILink>, ILinkProps>(
  function Link({ className, ...props }, ref) {
    return (
      <UILink
        ref={ref}
        {...props}
        className={linkStyle({ class: className })}
      />
    );
  },
);

type ILinkTextProps = React.ComponentProps<typeof UILink.Text> &
  VariantProps<typeof linkTextStyle> & { className?: string };

const LinkText = React.forwardRef<
  React.ComponentRef<typeof UILink.Text>,
  ILinkTextProps
>(function LinkText(
  { className, size = "md", action = "muted", ...props },
  ref,
) {
  return (
    <UILink.Text
      ref={ref}
      {...props}
      className={linkTextStyle({
        class: className,
        size,
        action,
        ...props,
      })}
    />
  );
});

Link.displayName = "Link";
LinkText.displayName = "LinkText";

export { Link, LinkText };
